import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    Pagination,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { UserService } from '../../services/user.service';
import { UserProfileService } from '../../services/user-profile.service';
import { InstitutionService } from '../../services/institution.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import EmployeeDocuments from './EmployeeDocuments';
import { IUserProfile } from '../../interfaces/user-profile.interface';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from './useRoleLabel';

interface User {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    type?: string;
}

const EmployeeDocumentAdmin: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [editingUsername, setEditingUsername] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [usernameSaving, setUsernameSaving] = useState(false);
    const [institutionCode, setInstitutionCode] = useState('');
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [checkingUsername, setCheckingUsername] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toast = useToast();
    const { t } = useTranslation(['users', 'common']);
    const roleLabel = useRoleLabel();

    useEffect(() => {
        const fetchInstitutionCode = async () => {
            try {
                const id = AuthService.getSelectedInstitution();
                const institution = await InstitutionService.getInstitutionById(id);
                setInstitutionCode(institution.code);
            } catch (err) {
                console.error('Failed to load institution code:', err);
            }
        };
        fetchInstitutionCode();
    }, []);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        fetchEmployees(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const fetchEmployees = async (page: number = 0) => {
        setLoading(true);
        try {
            const response = await UserService.getInstitutionUsers(page, 10, undefined, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements,
            });
        } catch (err) {
            toast.showApiError(err, t('employeeDocumentAdmin.loadEmployeesFailed'));
        } finally {
            setLoading(false);
        }
    };

    const usernamePrefix = institutionCode ? `${institutionCode}_` : '';

    const checkAvailability = (value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setUsernameAvailable(null);

        if (!value.trim() || !profile) {
            setCheckingUsername(false);
            return;
        }

        setCheckingUsername(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const fullUsername = usernamePrefix + value.trim();
                const available = await UserService.checkUsernameAvailable(fullUsername, profile.userId);
                setUsernameAvailable(available);
            } catch {
                setUsernameAvailable(null);
            } finally {
                setCheckingUsername(false);
            }
        }, 500);
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewUsername(value);
        checkAvailability(value);
    };

    const handleUsernameSave = async () => {
        if (!newUsername.trim() || !profile || usernameAvailable === false) return;
        setUsernameSaving(true);
        try {
            const fullUsername = usernamePrefix + newUsername.trim();
            await UserService.updateUsername(profile.userId, fullUsername);
            const updatedProfile = await UserProfileService.getProfileByUserId(profile.userId);
            setProfile(updatedProfile);
            setUsers(prev => prev.map(u =>
                u.id === profile.userId ? { ...u, username: fullUsername } : u
            ));
            setSelectedUser(prev => prev && prev.id === profile.userId ? { ...prev, username: fullUsername } : prev);
            setEditingUsername(false);
            setUsernameAvailable(null);
            toast.showSuccess(t('employeeDocumentAdmin.usernameUpdated'));
        } catch (err) {
            toast.showApiError(err, t('employeeDocumentAdmin.usernameUpdateFailed'));
        } finally {
            setUsernameSaving(false);
        }
    };

    useEffect(() => {
        if (!selectedUser) {
            setProfile(null);
            return;
        }
        setEditingUsername(false);
        setUsernameAvailable(null);
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const data = await UserProfileService.getProfileByUserId(selectedUser.id);
                setProfile(data);
            } catch (err) {
                toast.showApiError(err, t('employeeDocumentAdmin.loadProfileFailed'));
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser?.id]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchEmployees(page - 1);
    };

    const handleRowClick = (user: User) => {
        setSelectedUser(prev => prev?.id === user.id ? null : user);
    };

    return (
        <Box sx={{ maxWidth: 900, margin: '0 auto', padding: '20px' }}>
            <Typography variant="h5" gutterBottom>{t('employeeDocumentAdmin.title')}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TextField
                    label={t('manage.searchByUsername')}
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 260 }}
                />
                <Typography variant="body2" color="text.secondary">
                    {t('employeeDocumentAdmin.total', { total: pageInfo.totalElements })}
                </Typography>
            </Box>

            {loading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('manage.loading')}</Typography>
            ) : (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                        <thead>
                            <tr>
                                <th style={tableHeaderStyle}>{t('manage.columns.username')}</th>
                                <th style={tableHeaderStyle}>{t('employeeDocumentAdmin.firstName')}</th>
                                <th style={tableHeaderStyle}>{t('employeeDocumentAdmin.lastName')}</th>
                                <th style={tableHeaderStyle}>{t('manage.columns.type')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ ...tableCellStyle, textAlign: 'center', color: '#888' }}>
                                        {t('employeeDocumentAdmin.noEmployees')}
                                    </td>
                                </tr>
                            ) : users.map(user => (
                                <tr
                                    key={user.id}
                                    onClick={() => handleRowClick(user)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedUser?.id === user.id ? '#e3f2fd' : 'transparent',
                                    }}
                                >
                                    <td style={tableCellStyle}>{displayUsername(user.username)}</td>
                                    <td style={tableCellStyle}>{user.firstName || '-'}</td>
                                    <td style={tableCellStyle}>{user.lastName || '-'}</td>
                                    <td style={tableCellStyle}>{roleLabel(user.type) || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {pageInfo.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Pagination
                                count={pageInfo.totalPages}
                                page={pageInfo.currentPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {selectedUser && profile && !profileLoading && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>{t('employeeDocumentAdmin.profileInformation')}</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">{t('manage.columns.username')}</Typography>
                                {editingUsername ? (
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                value={newUsername}
                                                onChange={handleUsernameChange}
                                                disabled={usernameSaving}
                                                autoFocus
                                                slotProps={{
                                                    input: {
                                                        startAdornment: usernamePrefix ? (
                                                            <InputAdornment position="start">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {usernamePrefix}
                                                                </Typography>
                                                            </InputAdornment>
                                                        ) : undefined,
                                                    },
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={handleUsernameSave}
                                                disabled={usernameSaving || !newUsername.trim() || usernameAvailable === false || checkingUsername}
                                                aria-label={t('employeeDocumentAdmin.saveUsername')}
                                            >
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => { setEditingUsername(false); setUsernameAvailable(null); }}
                                                disabled={usernameSaving}
                                                aria-label={t('employeeDocumentAdmin.close')}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {newUsername.trim() && !checkingUsername && usernameAvailable !== null && (
                                            <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, color: usernameAvailable ? 'success.main' : 'error.main' }}
                                            >
                                                {usernameAvailable ? t('employeeDocumentAdmin.available') : t('employeeDocumentAdmin.usernameTaken')}
                                            </Typography>
                                        )}
                                        {checkingUsername && (
                                            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                {t('employeeDocumentAdmin.checking')}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body1">{displayUsername(profile.username) || '-'}</Typography>
                                        <IconButton
                                            size="small"
                                            disabled={!institutionCode}
                                            title={!institutionCode ? t('employeeDocumentAdmin.institutionCodeUnavailable') : t('employeeDocumentAdmin.editUsername')}
                                            aria-label={!institutionCode ? t('employeeDocumentAdmin.institutionCodeUnavailable') : t('employeeDocumentAdmin.editUsername')}
                                            onClick={() => {
                                                setNewUsername(displayUsername(profile.username));
                                                setEditingUsername(true);
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">{t('employeeDocumentAdmin.firstName')}</Typography>
                                <Typography variant="body1">{profile.firstName || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">{t('employeeDocumentAdmin.lastName')}</Typography>
                                <Typography variant="body1">{profile.lastName || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">{t('employeeDocumentAdmin.email')}</Typography>
                                <Typography variant="body1">{profile.email || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">{t('manage.columns.phone')}</Typography>
                                <Typography variant="body1">{profile.phone || '-'}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">{t('manage.userType')}</Typography>
                                <Typography variant="body1">{roleLabel(profile.type) || '-'}</Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {selectedUser && <EmployeeDocuments userId={selectedUser.id} />}

            {!selectedUser && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {t('employeeDocumentAdmin.clickRowHint')}
                </Typography>
            )}
        </Box>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd',
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd',
};

export default EmployeeDocumentAdmin;
